import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  ArrowUp,
  BookOpen,
  Headset,
  ImagePlus,
  Plus,
  Sparkles,
  X,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { sessionQueryKey, useSession, isPatient } from "@/lib/session";
import { senderRole, type ChatRole } from "@/lib/chatSender";
import { RichText } from "@/components/RichText";
import { TypedText } from "@/components/TypedText";
import { Button, LinkButton } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Alert } from "@/components/ui/Feedback";
import { useToast } from "@/components/ui/toast";
import { cn } from "@/lib/cn";

/* ─── API schemas ────────────────────────────────────────────────────── */

const chatResponseSchema = z.looseObject({
  reply: z.string().default(""),
  emergency: z.boolean().default(false),
  live_chat: z.boolean().default(false),
  ticket: z.string().nullable().default(null),
  status: z.string().nullable().default(null),
  action: z
    .object({ type: z.string(), url: z.string() })
    .nullable()
    .default(null),
  sources: z
    .array(
      z.looseObject({
        label: z.string(),
        detail: z.string().default(""),
        kind: z.string().default("general"),
      }),
    )
    .default([]),
});

type Source = { label: string; detail: string; kind: string };

const activeMessagesSchema = z.looseObject({
  ticket: z.string().nullable().default(null),
  status: z.string().nullable().default(null),
  messages: z
    .array(
      z.looseObject({
        sender: z.string(),
        content: z.string(),
        created_at: z.string().nullable().default(null),
      }),
    )
    .default([]),
});

/* ─── Types ──────────────────────────────────────────────────────────── */

type Message = {
  id: string;
  role: ChatRole;
  text: string;
  image?: string;
  emergency?: boolean;
  action?: { type: string; url: string } | null;
  failed?: boolean;
  sources?: Source[];
  /** Only the reply that just arrived animates; history renders instantly. */
  animate?: boolean;
};

/* ─── Quick suggestions shown on the empty state ─────────────────────── */

const SUGGESTIONS = [
  { label: "Jadwal dokter", prompt: "Apa saja jadwal praktik dokter di RS Mediora?" },
  { label: "Cara daftar", prompt: "Bagaimana cara mendaftar sebagai pasien baru?" },
  { label: "Syarat BPJS", prompt: "Apa syarat berobat menggunakan BPJS?" },
  { label: "Cek kesehatan", prompt: "Saya ingin melakukan cek kesehatan mandiri." },
];

const MAX_IMAGE_BYTES = 4 * 1024 * 1024;

/* ═══════════════════════════════════════════════════════════════════════
   LandingScreen — a full-height chatbot, nothing else.
   ═══════════════════════════════════════════════════════════════════════ */

export function LandingScreen() {
  const { data: session } = useSession();
  const patient = isPatient(session) ? session : null;
  const queryClient = useQueryClient();
  const toast = useToast();

  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState("");
  const [image, setImage] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [ticket, setTicket] = useState<string | null>(null);
  const [liveChat, setLiveChat] = useState<{
    active: boolean;
    status: string | null;
  }>({ active: false, status: null });

  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  /* ── Restore any active conversation so a refresh keeps the thread ── */
  useEffect(() => {
    let cancelled = false;
    api
      .get("/api/chat/messages/active", activeMessagesSchema)
      .then((data) => {
        if (cancelled) return;
        setTicket(data.ticket);
        if (data.status && data.status !== "Ditangani bot") {
          setLiveChat({ active: true, status: data.status });
        }
        setMessages(
          data.messages.map((item, index) => ({
            id: `history-${index}`,
            role: senderRole(item.sender),
            text: item.content,
          })),
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── Auto-scroll ── */
  const scrollToBottom = useCallback((smooth = true) => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, sending, scrollToBottom]);

  /* ── Send ── */
  const send = useCallback(
    async (text: string, attachment: string | null) => {
      const trimmed = text.trim();
      if (!trimmed && !attachment) return;

      const outgoing: Message = {
        id: `user-${Date.now()}`,
        role: "user",
        text: trimmed,
        image: attachment ?? undefined,
      };
      setMessages((prev) => [...prev, outgoing]);
      setDraft("");
      setImage(null);
      setSending(true);

      try {
        const data = await api.post("/api/chat", chatResponseSchema, {
          message: trimmed,
          ...(attachment ? { image: attachment } : {}),
        });

        if (data.ticket) setTicket(data.ticket);
        if (data.live_chat) {
          setLiveChat({
            active: true,
            status: data.status ?? "Menunggu operator",
          });
        }
        void queryClient.invalidateQueries({ queryKey: sessionQueryKey });

        if (data.reply) {
          setMessages((prev) => [
            ...prev,
            {
              id: `bot-${Date.now()}`,
              role: "bot",
              text: data.reply,
              emergency: data.emergency,
              action: data.action,
              sources: data.sources,
              animate: true,
            },
          ]);
        }
      } catch (cause) {
        const apiError = cause instanceof ApiError ? cause : null;
        if (apiError?.isQuotaExceeded) {
          toast.warning({
            title: "Kuota chat habis",
            description: "Perbarui langganan untuk melanjutkan percakapan.",
          });
        }
        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            role: "bot",
            text:
              apiError?.message ??
              "Tidak dapat terhubung ke server. Coba lagi.",
            failed: true,
          },
        ]);
      } finally {
        setSending(false);
        inputRef.current?.focus();
      }
    },
    [queryClient, toast],
  );

  /* ── Handlers ── */
  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    void send(draft, image);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send(draft, image);
    }
  }

  function handleFile(file: File | undefined) {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      toast.warning({
        title: "Format tidak didukung",
        description: "Gunakan JPG, PNG, atau WebP.",
      });
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.warning({
        title: "Gambar terlalu besar",
        description: "Maksimal 4 MB.",
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImage(String(reader.result));
    reader.readAsDataURL(file);
  }

  async function startNewChat() {
    try {
      await api.post("/api/chat/reset", z.looseObject({}));
    } catch {
      /* clearing locally is still correct */
    }
    setMessages([]);
    setTicket(null);
    setLiveChat({ active: false, status: null });
    setDraft("");
    setImage(null);
    inputRef.current?.focus();
  }

  const empty = messages.length === 0;

  /* ═══════════════════════════════════════════════════════════════════
     Render — three vertical bands: status bar · thread · composer
     ═══════════════════════════════════════════════════════════════════ */
  return (
    <div className="flex h-full flex-col">
      {/* ─── Status bar ─────────────────────────────────────────────── */}
      <div className="border-hairline bg-canvas/85 sticky top-0 z-10 border-b backdrop-blur-md">
        <div className="mx-auto flex h-11 w-full max-w-3xl items-center gap-3 px-4 sm:px-6">
          <p className="text-content-subtle min-w-0 flex-1 truncate text-xs">
            {ticket
              ? `Tiket ${ticket}`
              : "Asisten kesehatan RS Mediora"}
          </p>

          {patient?.credits_remaining != null && (
            <Badge
              tone={patient.credits_remaining > 0 ? "accent" : "critical"}
            >
              {patient.credits_remaining} kredit
            </Badge>
          )}

          <Button
            size="sm"
            variant="ghost"
            icon={<Plus className="size-4" />}
            onClick={startNewChat}
          >
            Baru
          </Button>
        </div>
      </div>

      {/* ─── Thread ─────────────────────────────────────────────────── */}
      <div
        ref={threadRef}
        className="scrollbar-slim min-h-0 flex-1 overflow-y-auto"
      >
        <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
          {liveChat.active && (
            <Alert tone="info" title="Terhubung ke operator" className="mb-4">
              <span className="inline-flex items-center gap-1.5">
                <Headset aria-hidden className="size-3.5" />
                {liveChat.status ?? "Menunggu operator"} — balasan akan
                datang dari staf RS Mediora.
              </span>
            </Alert>
          )}

          {empty ? (
            /* ─── Empty state ─────────────────────────────────────── */
            <div className="flex flex-col items-center py-16 sm:py-24">
              {/* Animated avatar */}
              <div className="relative mb-8">
                {/* Outer glow ring */}
                <div className="absolute -inset-4 animate-pulse rounded-full bg-gradient-to-br from-flux-400/20 via-flux-500/10 to-transparent blur-xl" />
                <div className="bg-accent/10 border-accent/25 relative grid size-20 place-items-center rounded-3xl border shadow-accent">
                  <Sparkles className="text-accent size-9" />
                </div>
              </div>

              <h1 className="text-center text-2xl font-bold tracking-tight sm:text-3xl">
                Ada yang bisa dibantu?
              </h1>

              <p className="text-content-muted mt-3 max-w-md text-center text-sm leading-relaxed">
                Tanyakan jadwal dokter, prosedur pendaftaran, syarat BPJS,
                atau keluhan kesehatan Anda.
              </p>

              {/* Suggestion chips */}
              <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                {SUGGESTIONS.map(({ label, prompt }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => void send(prompt, null)}
                    className={cn(
                      "border-hairline bg-surface hover:border-accent/40 hover:bg-surface-raised",
                      "rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* ─── Messages ────────────────────────────────────────── */
            <ul className="space-y-4">
              {messages.map((msg) => (
                <li
                  key={msg.id}
                  className={cn(
                    "flex",
                    msg.role === "user" ? "justify-end" : "justify-start",
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[85%] min-w-0",
                      msg.role === "user" && "order-2",
                    )}
                  >
                    {msg.role !== "user" && (
                      <p className="eyebrow mb-1">
                        {msg.role === "agent"
                          ? "Operator"
                          : msg.role === "system"
                            ? "Catatan sistem"
                            : "Mediora AI"}
                      </p>
                    )}

                    <div
                      className={cn(
                        "rounded-2xl px-4 py-3 text-sm leading-relaxed",
                        msg.role === "user"
                          ? "bg-accent text-accent-content [&_a]:text-accent-content font-medium"
                          : msg.emergency
                            ? "bg-critical-tint text-critical-text border-critical-500/40 border"
                            : msg.failed
                              ? "bg-monitor-tint text-monitor-text border-monitor-500/30 border"
                              : "bg-surface border-hairline border",
                      )}
                    >
                      {msg.image && (
                        <img
                          src={msg.image}
                          alt="Lampiran yang Anda kirim"
                          className="mb-2 max-h-56 w-auto rounded-lg"
                        />
                      )}
                      {msg.text &&
                        (msg.role === "bot" && !msg.failed ? (
                          <TypedText
                            text={msg.text}
                            enabled={Boolean(msg.animate)}
                            onTick={scrollToBottom}
                          />
                        ) : (
                          <RichText text={msg.text} />
                        ))}
                    </div>

                    {msg.sources && msg.sources.length > 0 && (
                      <ul className="mt-2 flex flex-wrap gap-1.5">
                        {msg.sources.map((src, i) => (
                          <li
                            key={i}
                            title={src.detail}
                            className={cn(
                              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-2xs",
                              src.kind === "hospital"
                                ? "bg-info-tint text-info-text"
                                : "bg-surface-sunken text-content-subtle",
                            )}
                          >
                            <BookOpen aria-hidden className="size-3" />
                            {src.label}
                          </li>
                        ))}
                      </ul>
                    )}

                    {msg.action?.url && (
                      <div className="mt-2">
                        <LinkButton
                          to={msg.action.url}
                          size="sm"
                          variant="primary"
                        >
                          Buka pemeriksaan
                        </LinkButton>
                      </div>
                    )}
                  </div>
                </li>
              ))}

              {/* Typing indicator */}
              {sending && (
                <li className="flex justify-start">
                  <div className="bg-surface border-hairline rounded-2xl border px-4 py-3">
                    <span className="sr-only">Mediora sedang mengetik</span>
                    <span aria-hidden className="flex gap-1">
                      {[0, 150, 300].map((delay) => (
                        <span
                          key={delay}
                          className="bg-content-subtle size-1.5 animate-bounce rounded-full"
                          style={{ animationDelay: `${delay}ms` }}
                        />
                      ))}
                    </span>
                  </div>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>

      {/* ─── Composer ───────────────────────────────────────────────── */}
      <div className="border-hairline bg-canvas border-t">
        <form
          onSubmit={handleSubmit}
          className="mx-auto w-full max-w-3xl px-4 py-3 sm:px-6"
        >
          {image && (
            <div className="mb-2 flex items-center gap-2">
              <img
                src={image}
                alt="Pratinjau lampiran"
                className="size-12 rounded-lg object-cover"
              />
              <button
                type="button"
                onClick={() => setImage(null)}
                className="text-content-subtle hover:text-content inline-flex items-center gap-1 text-xs"
              >
                <X className="size-3.5" />
                Hapus lampiran
              </button>
            </div>
          )}

          <div className="border-hairline-strong bg-surface focus-within:border-accent/50 flex items-end gap-2 rounded-2xl border p-2 transition-colors">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="sr-only"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              aria-label="Lampirkan gambar"
              className="text-content-subtle hover:bg-surface-sunken hover:text-content grid size-9 shrink-0 place-items-center rounded-xl"
            >
              <ImagePlus className="size-[18px]" />
            </button>

            <textarea
              ref={inputRef}
              rows={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Tulis pertanyaan Anda…"
              aria-label="Pesan"
              className="text-content placeholder:text-content-subtle max-h-40 min-h-9 flex-1 resize-none bg-transparent py-2 text-sm outline-none"
            />

            <Button
              type="submit"
              variant="primary"
              size="sm"
              loading={sending}
              disabled={!draft.trim() && !image}
              aria-label="Kirim pesan"
              className="size-9 shrink-0 p-0"
              icon={
                sending ? undefined : <ArrowUp className="size-4" />
              }
            />
          </div>

          <p className="text-content-subtle mt-2 text-center text-2xs">
            Mediora AI dapat keliru. Untuk keadaan darurat hubungi{" "}
            <b>119</b> atau IGD terdekat.
          </p>
        </form>
      </div>
    </div>
  );
}
